import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { usePageParams } from '@/App';
import { setCanonical, setMeta } from '@/utils/seo';
import { INDUSTRY_MAP, INDUSTRIES } from '@/data/industries';
import LeadForm from '@/components/LeadForm';
import HeroBackground from '@/components/HeroBackground';
import { ServiceSchema, FAQSchema, HowToSchema, SpeakableSchema, BreadcrumbSchema } from '@/components/SchemaMarkup';
import { MapPin, ArrowRight, Search, Loader2, AlertTriangle, DollarSign, FileText, Star, Lightbulb, Plus } from 'lucide-react';
import api from '@/utils/api';

const POPULAR = [
  'Los Angeles','San Diego','San Jose','San Francisco','Fresno','Sacramento',
  'Long Beach','Oakland','Bakersfield','Anaheim','Santa Ana','Riverside',
  'Stockton','Irvine','Chula Vista','Fremont','San Bernardino','Modesto',
  'Oxnard','Fontana','Moreno Valley','Glendale','Huntington Beach','Pasadena'
];

export default function IndustryHubPage() {
  const { industrySlug } = usePageParams();
  const ind = INDUSTRY_MAP[industrySlug];
  const [cities, setCities] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [authority, setAuthority] = useState('');
  const [search, setSearch] = useState('');
  const [customCity, setCustomCity] = useState('');
  const [creating, setCreating] = useState(false);
  const [createMsg, setCreateMsg] = useState('');
  const [tip, setTip] = useState('');
  const [cityOfWeek, setCityOfWeek] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!ind) return;
    setCanonical(`/workers-comp-for-${industrySlug}-companies`);
    setMeta(`Workers Comp for ${ind.name} Companies in California | CompareWorkersComp.com`,
      `Compare workers comp insurance for ${ind.name.toLowerCase()} companies in California. ${ind.desc}`);
    api.get('/cities?limit=500').then(r => setCities(r.data.cities || [])).catch(() => {});
    api.get(`/faqs/${industrySlug}?city=California`).then(r => setFaqs(r.data.faqs || [])).catch(() => {});
    api.get(`/authority/${industrySlug}?city=California`).then(r => setAuthority(r.data.content || '')).catch(() => {});
    api.get('/tip-of-the-week').then(r => setTip(r.data.tip)).catch(() => {});
    api.get('/city-of-the-week').then(r => setCityOfWeek(r.data)).catch(() => {});
  }, [industrySlug, ind]);

  if (!ind) return <div className="min-h-screen flex items-center justify-center text-gray-500">Industry not found</div>;

  const filtered = search ? cities.filter(c => c.name.toLowerCase().includes(search.toLowerCase())) : [];
  const slugify = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  const handleCityCreate = async () => {
    if (!customCity.trim()) return;
    setCreating(true);
    try {
      const { data } = await api.post('/cities/lookup', { name: customCity.trim() });
      if (data.city) {
        setCreateMsg('Redirecting...');
        setTimeout(() => navigate(`/workers-comp-for-${industrySlug}-in-${data.city.slug}-ca`), 800);
      }
    } catch { setCreateMsg('Could not find that city. Please try again.'); }
    setCreating(false);
  };

  // WCIRB info per industry
  const wcirbCodes = ind.slug === 'construction' ? '5645 (Residential framing), 5436 (Finish carpentry), 5201 (Concrete)' : ind.slug === 'restaurants' ? '9082 (Fast food), 9083 (Full service)' : ind.slug === 'home-health' ? '8835 (Home health aides), 8827 (Visiting nurses)' : ind.slug === 'landscaping' ? '0042 (Landscape gardening), 0050 (Tree trimming)' : ind.slug === 'trucking' ? '7231 (Long haul), 7229 (Local delivery)' : 'Varies by specific business classification';

  return (
    <>
      <ServiceSchema name={`Workers Comp for ${ind.name} in California`} description={ind.desc} areaServed="California" />
      {faqs.length > 0 && <FAQSchema faqs={faqs} />}
      <HowToSchema />
      <SpeakableSchema name={`Workers Comp for ${ind.name} Companies`} url={`https://compareworkerscomp.com/workers-comp-for-${industrySlug}-companies`} />
      <BreadcrumbSchema items={[{ name: 'Home', url: '/' }, { name: ind.name }]} />

      {/* Hero with video background */}
      <section className="relative pt-24 pb-16 overflow-hidden">
        <HeroBackground />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="speakable-intro text-white/30 text-xs mb-3">{`Workers compensation insurance for ${ind.name.toLowerCase()} companies in California. Compare quotes from 50+ carriers, learn about WCIRB class codes, and find coverage tailored to ${ind.name.toLowerCase()} businesses.`}</p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
            <div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white leading-tight mb-6 hero-text-shadow" data-testid="industry-h1">Workers Comp for <span className="text-red-500">{ind.name}</span> in California</h1>
              <p className="text-white/80 text-lg mb-6 hero-text-shadow-sm">{ind.desc}</p>
              {/* Quick facts */}
              <div className="space-y-2 mb-6">
                <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2.5 border border-white/10">
                  <DollarSign className="w-4 h-4 text-green-400 shrink-0" />
                  <span className="text-white/80 text-sm">Average cost: <strong className="text-white">{ind.avgCost}</strong></span>
                </div>
                <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2.5 border border-white/10">
                  <FileText className="w-4 h-4 text-blue-400 shrink-0" />
                  <span className="text-white/80 text-sm">WCIRB codes: <strong className="text-white">{wcirbCodes}</strong></span>
                </div>
                <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2.5 border border-white/10">
                  <AlertTriangle className="w-4 h-4 text-red-400 shrink-0" />
                  <span className="text-white/80 text-sm">CA requires WC for <strong className="text-white">every employer with 1+ employee</strong></span>
                </div>
              </div>
              <a href="#lead-form" onClick={() => { if (window.gtag) window.gtag('event', 'renewal_cta_click', { event_category: 'engagement', event_label: 'industry_page' }); }} className="renewal-pulse inline-flex items-center gap-2 bg-green-500/20 backdrop-blur-sm text-white px-4 py-2.5 rounded-full border-2 border-green-400/40 hover:bg-green-500/30 transition-colors text-sm font-medium mt-2" data-testid="industry-renewal-cta">
                Rates Change — Compare At Renewal
              </a>
            </div>
            <div id="lead-form">
              <LeadForm industry={industrySlug} source="industry-hub" />
            </div>
          </div>
        </div>
      </section>

      {/* Authority Section */}
      {authority && (
        <section className="bg-white py-14" data-testid="industry-authority">
          <div className="max-w-3xl mx-auto px-4">
            <h2 className="text-2xl font-bold text-cwc-navy mb-4">What California {ind.name} Need to Know</h2>
            <p className="text-gray-600 leading-relaxed">{authority}</p>
          </div>
        </section>
      )}

      {/* Search for your city in this industry */}
      <section className="bg-gray-50 py-14" data-testid="industry-city-search">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold text-cwc-navy mb-2">Find {ind.name} Workers Comp in Your City</h2>
          <p className="text-gray-500 mb-6">Select your city to see {ind.name.toLowerCase()}-specific workers comp info, costs, and get a quote</p>
          <div className="max-w-md mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input type="text" placeholder={`Search for your city...`} value={search} onChange={e => { setSearch(e.target.value); setCustomCity(e.target.value); setCreateMsg(''); }} onKeyDown={e => e.key === 'Enter' && filtered.length === 0 && search.length > 1 && handleCityCreate()} className="w-full pl-12 pr-4 py-4 rounded-xl text-base border border-gray-200 shadow-sm focus:ring-2 focus:ring-red-400 outline-none" data-testid="industry-search-input" />
          </div>
          {search && filtered.length > 0 && (
            <div className="max-w-md mx-auto mt-2 bg-white rounded-xl shadow-lg max-h-64 overflow-y-auto text-left border border-gray-100">
              {filtered.slice(0, 12).map(c => (
                <Link key={c.slug} to={`/workers-comp-for-${industrySlug}-in-${c.slug}-ca`} className="flex items-center justify-between px-4 py-3 hover:bg-red-50 border-b border-gray-50 transition-colors">
                  <span className="flex items-center gap-2"><MapPin className="w-4 h-4 text-red-500" /><span className="text-cwc-navy font-medium text-sm">{c.name}, CA</span></span>
                  <span className="text-xs text-gray-400">{ind.name}</span>
                </Link>
              ))}
            </div>
          )}
          {/* Auto-create when city not found */}
          {search && search.length > 1 && filtered.length === 0 && (
            <div className="max-w-md mx-auto mt-4 bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
              <div className="flex items-center gap-2 mb-3 justify-center">
                <Plus className="w-5 h-5 text-green-600" />
                <p className="text-cwc-navy font-semibold text-sm">Don't see your city? We'll create it!</p>
              </div>
              <button onClick={handleCityCreate} disabled={creating} className="bg-red-600 text-white px-6 py-2.5 rounded-lg text-sm font-bold hover:bg-red-700 disabled:opacity-50 flex items-center gap-2 mx-auto transition-colors" data-testid="industry-create-city-btn">
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Create {ind.name} Page for "{search}"
              </button>
              {createMsg && <p className="text-sm mt-3 text-green-600 font-medium">{createMsg}</p>}
            </div>
          )}
          {/* Green hint button when not searching */}
          {!search && (
            <div className="mt-5">
              <button onClick={() => document.querySelector('[data-testid="industry-search-input"]').focus()} className="inline-flex items-center gap-2 bg-green-600 text-white text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-green-700 transition-colors" data-testid="industry-cant-find-btn">
                <Plus className="w-4 h-4" /> Can't find your city? Type it — we'll build it!
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Popular Cities for this industry */}
      <section className="bg-white py-14" data-testid="industry-popular-cities">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-cwc-navy mb-8">{ind.name} Workers Comp by City</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {POPULAR.map(name => {
              const slug = slugify(name);
              return (
                <Link key={slug} to={`/workers-comp-for-${industrySlug}-in-${slug}-ca`} className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-3.5 hover:bg-red-50 border border-gray-100 hover:border-red-200 transition-all group" data-testid={`ind-city-${slug}`}>
                  <span className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-red-500 shrink-0" />
                    <span className="text-cwc-navy font-medium text-sm group-hover:text-red-600 transition-colors">{name}, CA</span>
                  </span>
                  <ArrowRight className="w-3.5 h-3.5 text-gray-300 group-hover:text-red-400 transition-colors" />
                </Link>
              );
            })}
          </div>
          <div className="text-center mt-8">
            <Link to="/cities" className="text-red-600 font-medium text-sm hover:underline">View all {cities.length}+ California cities &rarr;</Link>
          </div>
        </div>
      </section>

      {/* City of the Week + Tip of the Week */}
      <section className="bg-white py-14" data-testid="industry-weekly-spotlight">
        <div className="max-w-5xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {cityOfWeek && (
              <div className="bg-green-50 rounded-2xl p-7 border border-green-200" data-testid="industry-city-of-week">
                <div className="inline-flex items-center gap-2 bg-red-600 text-white px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-5">
                  <Star className="w-3.5 h-3.5" /> City of the Week
                </div>
                <h3 className="text-2xl font-bold text-green-900 mb-2">{cityOfWeek.name}, CA</h3>
                <p className="text-green-700/60 text-sm mb-5">{cityOfWeek.county} | Pop. {cityOfWeek.pop}</p>
                <div className="flex flex-wrap gap-2">
                  <Link to={`/workers-comp-for-${industrySlug}-in-${cityOfWeek.slug}-ca`} className="bg-green-600 text-white text-xs font-bold px-3 py-1.5 rounded-lg hover:bg-green-700 transition-colors" data-testid="industry-cotw-link">
                    {ind.name} in {cityOfWeek.name} <ArrowRight className="w-3 h-3 inline ml-1" />
                  </Link>
                  <Link to={`/workers-comp-insurance-${cityOfWeek.slug}-ca`} className="bg-green-100 text-green-700 text-xs font-medium px-3 py-1.5 rounded-lg hover:bg-green-200 transition-colors border border-green-200">
                    All Coverage
                  </Link>
                </div>
              </div>
            )}
            {tip && (
              <div className="bg-green-50 rounded-2xl p-7 border border-green-200" data-testid="industry-tip-of-week">
                <div className="inline-flex items-center gap-2 bg-red-600 text-white px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-5">
                  <Lightbulb className="w-3.5 h-3.5" /> Workers' Comp Tip of the Week
                </div>
                <p className="text-green-900 text-lg leading-relaxed font-medium">{tip}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* FAQ */}
      {faqs.length > 0 && (
        <section className="bg-gray-50 py-14">
          <div className="max-w-3xl mx-auto px-4">
            <h2 className="text-2xl font-bold text-cwc-navy mb-8">{ind.name} Workers Comp FAQ</h2>
            {faqs.map((f, i) => (
              <div key={i} className="mb-4 bg-white rounded-xl p-5 border border-gray-200">
                <h3 className="font-semibold text-cwc-navy mb-2">{f.q}</h3>
                <p className="text-gray-600 text-sm">{f.a}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Carriers + Featured In */}
      <section className="bg-gray-50 py-10">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-xs uppercase tracking-widest text-gray-400 font-medium mb-3">Our Partners — Trusted Insurance Carriers</p>
          <img src="/carriers.png" alt="Insurance Carriers" className="max-w-full mx-auto opacity-80 mb-8" loading="lazy" />
          <p className="text-xs uppercase tracking-widest text-gray-400 font-medium mb-3">Trio Insurance Featured In</p>
          <img src="/featured-in.png" alt="Featured in FOX, Google News, Digital Journal" className="max-w-xs mx-auto" loading="lazy" />
        </div>
      </section>

      {/* Other Industries */}
      <section className="bg-white py-14">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-xl font-bold text-cwc-navy mb-6 text-center">Other Industries We Cover</h2>
          <div className="flex flex-wrap justify-center gap-3">
            {INDUSTRIES.filter(i => i.slug !== industrySlug).map(i => (
              <Link key={i.slug} to={`/workers-comp-for-${i.slug}-companies`} className="bg-gray-50 rounded-full px-5 py-2.5 text-sm text-cwc-navy font-medium hover:bg-red-50 hover:text-red-600 border border-gray-100 transition-colors">{i.name}</Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-cwc-navy py-14">
        <div className="max-w-3xl mx-auto text-center px-4">
          <h2 className="text-2xl font-bold text-white mb-4">Compare {ind.name} Workers Comp Quotes Now</h2>
          <a href="#lead-form" className="inline-flex bg-red-600 text-white font-bold px-8 py-3 rounded-full hover:bg-red-700 transition-colors">Get Free Quotes <ArrowRight className="w-4 h-4 ml-2" /></a>
        </div>
      </section>
    </>
  );
}
